/* eslint-disable react/jsx-no-undef */
/* eslint-disable react/react-in-jsx-scope */
import { noop } from 'lodash';
import { useMemo } from 'react';

import { Diagnosis, DiagnosisQuery } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { createTranslatedText, dateFormatter, Ordering } from '@dreryk/react-common';
import {
   AlignItems,
   CheckIcon,
   createDynamicColumnsWidth,
   IconFillColor,
   IconSize,
   IconWrapper,
   JustifyContent,
   SecondaryText,
   StyledFlex,
   TableColumnConfiguration,
   Text,
   ValueRendererProps,
} from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { ConfidentialMask, getConfidentialAccessButton } from '~libs.desktop.shared.medical-data-confidentiality';
import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';

import { patientMedicalInformationTranslationKeys } from '../../translations';

import { OptionsCell } from './cells/OptionsCell';

const DEFAULT_ORDERINGS: Ordering<Diagnosis>[] = [
   {
      descending: true,
      property: 'creationDate',
   },
];

export const useDiagnosisInformationDataConfiguration = (
   patientId: number
): LazyLoadedDataConfigurationByCqrsQuery<Diagnosis> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<Diagnosis>(() => {
      const query = new DiagnosisQuery();
      query.patientId = Slot.emptyIfNull(patientId);
      return query;
   }, DEFAULT_ORDERINGS);
};

type UseDiagnosisInformationTableColumnsOptions = {
   patientId: number;
};

// eslint-disable-next-line max-lines-per-function
export const useDiagnosisInformationTableColumns = (
   options: UseDiagnosisInformationTableColumnsOptions
): TableColumnConfiguration<Diagnosis>[] => {
   // eslint-disable-next-line max-lines-per-function
   return useMemo((): TableColumnConfiguration<Diagnosis>[] => {
      const columnsWidth = createDynamicColumnsWidth({
         creationDate: '102px',
         createdBy: '360px',
         diagnosis: '100%',
         main: '76px',
         chronic: '100px',
         options: '215px',
      });

      return [
         {
            accessor: 'creationDate',
            label: patientMedicalInformationTranslationKeys.diagnosis.table.creationDate,
            width: columnsWidth.creationDate,
            formatter: dateFormatter,
         },

         {
            name: 'createdBy',
            accessor: noop,
            label: patientMedicalInformationTranslationKeys.diagnosis.table.createdBy,
            width: columnsWidth.createdBy,
            sortingDisabled: true,
            renderer: ({ bean: { createdBy } }: ValueRendererProps<Diagnosis>) => {
               if (!createdBy) {
                  return null;
               }

               return (
                  <PractitionerSafePresenter
                     testId={'author'}
                     practitioner={createdBy}
                     type={'standalone'}
                     withIdentification
                  />
               );
            },
         },
         {
            name: 'diagnosis',
            accessor: noop,
            label: patientMedicalInformationTranslationKeys.chronicDisease.table.diagnosis,
            width: columnsWidth.diagnosis,
            sortingDisabled: true,
            renderer: ({ bean }: ValueRendererProps<Diagnosis>) => (
               <ConfidentialMask entry={bean}>
                  {() => (
                     <StyledFlex direction={'column'}>
                        {bean.icd10 ? (
                           <Text testId={'diagnosis'}>
                              {`${bean.icd10.getCode().asString()} ${bean.icd10.getName().asString()}`}
                           </Text>
                        ) : null}
                        {bean.additionalInformation ? (
                           <SecondaryText
                              compact
                              testId={'additionalInformation'}
                              text={createTranslatedText(bean.additionalInformation)}
                           />
                        ) : null}
                     </StyledFlex>
                  )}
               </ConfidentialMask>
            ),
         },
         {
            accessor: 'main',
            label: patientMedicalInformationTranslationKeys.diagnosis.table.main,
            width: columnsWidth.main,
            justifyContent: JustifyContent.CENTER,
            alignItems: AlignItems.CENTER,
            sortingDisabled: true,
            renderer: ({ bean: { main } }: ValueRendererProps<Diagnosis>) => {
               if (!main) {
                  return null;
               }

               return (
                  <IconWrapper size={IconSize.SMALL} testId={'main'} fillColor={IconFillColor.PRIMARY}>
                     <CheckIcon />
                  </IconWrapper>
               );
            },
         },
         {
            accessor: 'chronic',
            label: patientMedicalInformationTranslationKeys.diagnosis.table.chronic,
            width: columnsWidth.chronic,
            justifyContent: JustifyContent.CENTER,
            alignItems: AlignItems.CENTER,
            sortingDisabled: true,
            renderer: ({ bean: { chronic } }: ValueRendererProps<Diagnosis>) => {
               if (!chronic) {
                  return null;
               }

               return (
                  <IconWrapper size={IconSize.SMALL} testId={'chronic'} fillColor={IconFillColor.PRIMARY}>
                     <CheckIcon />
                  </IconWrapper>
               );
            },
         },
         {
            name: 'options',
            label: '',
            width: columnsWidth.options,
            alignItems: AlignItems.CENTER,
            justifyContent: JustifyContent.CENTER,
            accessor: noop,
            sortingDisabled: true,
            renderer: ({ bean }: ValueRendererProps<Diagnosis>) => {
               const confidentialButton = getConfidentialAccessButton(bean, {
                  resourceType: 'MEDICAL_INFORMATION',
                  refetchQuery: DiagnosisQuery,
               });

               if (confidentialButton) {
                  return confidentialButton;
               }

               return <OptionsCell visitId={bean.visitId?.asNumber()} patientId={options.patientId} />;
            },
         },
      ];
   }, [options.patientId]);
};
